import React, { useState, useRef, useEffect, lazy, Suspense } from 'react';
import { useNotification } from '../../context/NotificationContext';
import { FaBell } from '@react-icons/all-files/fa/FaBell';
import { Link, useNavigate } from 'react-router-dom';
import { toAppPath } from '../../feed/shared/links.js';
const ReactMarkdown = lazy(() => import('react-markdown'));

const NotificationCenter = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
  } = useNotification();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const toggleDropdown = () => setIsOpen(!isOpen);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // BAWS.in links (and /paths) open in place, in the app too; only other
  // sites open a new tab.
  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    const { url } = notification;
    if (!url) return;
    const path = toAppPath(url);
    if (path) {
      setIsOpen(false);
      navigate(path);
    } else if (/^https?:\/\//i.test(url)) {
      window.open(url, '_blank', 'noopener');
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={toggleDropdown}
        className="relative p-2 text-gray-600 hover:text-blue-600 transition-colors duration-300 focus:outline-none"
        aria-label="Notifications"
      >
        <FaBell size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-red-100 transform translate-x-1/4 -translate-y-1/4 bg-red-600 rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-100">
          <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-700">
              Notifications
            </h3>
            {notifications.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={markAllAsRead}
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  Mark all read
                </button>
                <button
                  onClick={clearNotifications}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              </div>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No new notifications
              </div>
            ) : (
              <ul>
                {notifications.map((notification) => (
                  <li
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-200 ${
                      !notification.read ? 'bg-blue-50' : ''
                    }`}
                  >
                    <div className="flex gap-3">
                      {notification.image && (
                        <div className="flex-shrink-0">
                          <img
                            src={notification.image}
                            alt=""
                            className="h-10 w-10 rounded object-cover"
                          />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p
                          className={`text-sm font-medium ${!notification.read ? 'text-gray-900' : 'text-gray-600'}`}
                        >
                          {notification.title}
                        </p>
                        <div className="text-xs text-gray-500 mt-0.5 line-clamp-2">
                          <Suspense fallback={notification.body}>
                            <ReactMarkdown>{notification.body}</ReactMarkdown>
                          </Suspense>
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(notification.id).toLocaleTimeString([], {
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      </div>
                      {!notification.read && (
                        <div className="flex-shrink-0 self-center">
                          <span className="block h-2 w-2 bg-blue-600 rounded-full"></span>
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {notifications.length > 0 && (
            <div className="px-4 py-2 bg-gray-50 text-center border-t border-gray-100">
              <Link
                to="/notifications"
                onClick={() => setIsOpen(false)}
                className="text-xs text-blue-600 hover:text-blue-800 font-medium"
              >
                View all
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationCenter;
